import { createSharedComposable, isDefined, useCssVar } from "@vueuse/core"
import { useConcept } from "~/composables/concept"
import { useLocation } from "~/composables/locations"
import { kebabCase } from "~/lib/case"
import { TavoParams, type TavoConfig } from "."

// NOTE: set up to avoid loading tavo unless configured
const TavoConfigs = computed<TavoConfig[]>(() => [
    {
        group: 1, // "memento-mori-hospitality",
        concept: "jaime-taqueria",
        fonts: [
            {
                family: "Cooper",
                src: "url(https://jaimetaqueria.com/fonts/Cooper-Regular.woff2)",
                style: "normal",
                weight: "400"
            },
            {
                family: "Cooper",
                src: "url(https://jaimetaqueria.com/fonts/Cooper-Medium.woff2)",
                style: "normal",
                weight: "500"
            },
            {
                family: "Cooper",
                src: "url(https://jaimetaqueria.com/fonts/Cooper-SemiBold.woff2)",
                style: "normal",
                weight: "600"
            },
            {
                family: "Cooper",
                src: "url(https://jaimetaqueria.com/fonts/Cooper-Bold.woff2)",
                style: "normal",
                weight: "700"
            },
            {
                family: "Cooper",
                src: "url(https://jaimetaqueria.com/fonts/Cooper-ExtraBold.woff2)",
                style: "normal",
                weight: "800"
            },
            {
                family: "Cooper",
                src: "url(https://jaimetaqueria.com/fonts/Cooper-Black.woff2)",
                style: "normal",
                weight: "900"
            }
        ],
        styles: {
            background: useCssVar("--cream").value,
            foreground: useCssVar("--black").value,
            primary: useCssVar("--green").value,
            primaryForeground: useCssVar("--cream").value,
            card: useCssVar("--lightgrey").value,
            cardForeground: useCssVar("--darkgrey").value,
            muted: useCssVar("--lightgrey").value,
            mutedForeground: useCssVar("--darkgrey").value,
            loud: useCssVar("--pink").value,
            loudForeground: useCssVar("--cream").value,
            border: useCssVar("--grey").value,
            input: useCssVar("--grey").value,
            bodyFontFamily: useCssVar("--body-font-family").value,
            headingFontFamily: useCssVar("--heading-font-family").value,
            submitButtonRadius: "10px"
        }
    },
    {
        group: 1, // "memento-mori-hospitality",
        concept: "jame-enoteca",
        fonts: [
            {
                family: "SupriaSans",
                src: "url(https://eatjame.com/fonts/SupriaSans-Medium.otf)",
                style: "normal",
                weight: "500"
            },
            {
                family: "SupriaSans",
                src: "url(https://eatjame.com/fonts/SupriaSans-Heavy.otf)",
                style: "normal",
                weight: "900"
            }
        ],
        styles: {
            background: useCssVar("--cream").value,
            foreground: useCssVar("--black").value,
            primary: useCssVar("--teal").value,
            primaryForeground: useCssVar("--cream").value,
            card: useCssVar("--lightgrey").value,
            cardForeground: useCssVar("--darkgrey").value,
            muted: useCssVar("--lightgrey").value,
            mutedForeground: useCssVar("--darkgrey").value,
            loud: useCssVar("--copper").value,
            loudForeground: useCssVar("--cream").value,
            border: useCssVar("--grey").value,
            input: useCssVar("--grey").value,
            bodyFontFamily: useCssVar("--body-font-family").value,
            headingFontFamily: useCssVar("--heading-font-family").value,
            submitButtonRadius: "10px"
        }
    },
    {
        group: 1, // "memento-mori-hospitality",
        concept: "jemma",
        fonts: [
            {
                family: "ArtifexCF",
                src: "url(https://jemmarestaurants.com/fonts/ArtifexCF-Book.otf)",
                style: "normal",
                weight: "400"
            },
            {
                family: "ArtifexCF",
                src: "url(https://jemmarestaurants.com/fonts/ArtifexCF-DemiBold.otf)",
                style: "normal",
                weight: "600"
            },
            {
                family: "ArtifexCF",
                src: "url(https://jemmarestaurants.com/fonts/ArtifexCF-Bold.otf)",
                style: "normal",
                weight: "700"
            },
            {
                family: "ArtifexCF",
                src: "url(https://jemmarestaurants.com/fonts/ArtifexCF-Heavy.otf)",
                style: "normal",
                weight: "900"
            }
        ],
        styles: {
            background: useCssVar("--cream").value,
            foreground: useCssVar("--black").value,
            primary: useCssVar("--forestgreen").value,
            primaryForeground: useCssVar("--cream").value,
            card: useCssVar("--lightgrey").value,
            cardForeground: useCssVar("--darkgrey").value,
            muted: useCssVar("--lightgrey").value,
            mutedForeground: useCssVar("--darkgrey").value,
            loud: useCssVar("--burgundy").value,
            loudForeground: useCssVar("--cream").value,
            border: useCssVar("--grey").value,
            input: useCssVar("--grey").value,
            bodyFontFamily: useCssVar("--body-font-family").value,
            headingFontFamily: useCssVar("--heading-font-family").value,
            submitButtonRadius: "10px"
        }
    },
    {
        group: 1, // "memento-mori-hospitality",
        concept: "ospi",
        fonts: [
            {
                family: "Optima",
                src: "url(https://ospi.com/fonts/Optima-Regular.ttf)",
                style: "normal",
                weight: "400"
            },
            {
                family: "Optima",
                src: "url(https://ospi.com/fonts/Optima-Bold.ttf)",
                style: "normal",
                weight: "700"
            },
            {
                family: "Optima",
                src: "url(https://ospi.com/fonts/Optima-Black.ttf)",
                style: "normal",
                weight: "900"
            }
        ],
        styles: {
            background: useCssVar("--cream").value,
            foreground: useCssVar("--black").value,
            primary: useCssVar("--midnight").value,
            primaryForeground: useCssVar("--cream").value,
            card: useCssVar("--lightgrey").value,
            cardForeground: useCssVar("--darkgrey").value,
            muted: useCssVar("--lightgrey").value,
            mutedForeground: useCssVar("--darkgrey").value,
            loud: useCssVar("--red").value,
            loudForeground: useCssVar("--cream").value,
            border: useCssVar("--grey").value,
            input: useCssVar("--grey").value,
            bodyFontFamily: useCssVar("--body-font-family").value,
            headingFontFamily: useCssVar("--heading-font-family").value,
            submitButtonRadius: "10px"
        }
    }
])

export const useTavoParams = createSharedComposable(() => {
    const { concept } = useConcept()
    const { location } = useLocation()

    return computed<TavoParams>(() => ({
        group: 1,
        concept: concept.value.slug,
        location: isDefined(location) ? kebabCase(location.value.name) : undefined
    }))
})

export const useTavoConfig = createSharedComposable(() => {
    const tavoParams = useTavoParams()

    return computed<TavoConfig | undefined>(() => {
        const locationConfig = TavoConfigs.value.find(c => c.concept === tavoParams.value.concept && c.location === tavoParams.value.location)
        const conceptConfig = TavoConfigs.value.find(c => c.concept === tavoParams.value.concept)
        // const groupConfig = TavoConfigs.value.find(c => c.group === concept.value.parentCompany)
        return locationConfig || conceptConfig // || groupConfig
    })
})
